import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  TextInput,
  FormGroup,
  RadioButton,
  RadioButtonGroup,
  FormLabel,
} from '@carbon/react';

import styles from './MicastAssignModalForm.module.scss';

import cx from 'classnames';
import { WizardForm, WizardStep } from '../../components/Wizard/WizardForm';
import { checkDeviceKey, useAssignMirrorDeviceMutation } from '@/redux/new_services/device';
import { showToast } from '@/redux/new_services/toasts';

const AssignMirrorWizzard = ({ mirrorServerDeviceID, mirrorServerName, hideModal }) => {
  const dispatch = useDispatch();

  const [
    assignDevice,
    {
      isLoading: isSaving,
      error,
    }
  ] = useAssignMirrorDeviceMutation();

  const [keyCheckFailure, setKeyCheckFailure] = useState(false);
  // Loading-Overlay only during async work (key check, submit), not on mount
  const [loading, setLoading] = useState(false);
  const [stateItem, setItem] = useState({
    label: '',
    key: '',
    orientation: 0,
  });
  const [keyChecked, setKeyChecked] = useState(true);
  const [labelChecked, setLabelChecked] = useState(true);

  const doCheckDeviceKey = (key) => {
    setLoading(true);
    setKeyChecked(false);
    checkDeviceKey(key).then(data => {
      setKeyCheckFailure(!data);
      setLoading(false);
      setKeyChecked(data);
    });
  }

  const onChangeField = useCallback(
    (field, value) => {
      if (field === 'key') {
        if (stateItem.key !== value && keyChecked === true) {
          setKeyChecked(false);
        }
      }

      setItem({
        ...stateItem,
        [field]: value,
      });
    },

    [stateItem, setItem, keyChecked, setKeyChecked]
  );

  const handleSubmit = () => {
    setLoading(true);
    assignDevice({
      mirror_server_device_id: Number(mirrorServerDeviceID),
      key: stateItem.key,
      label: stateItem.label,
      orientation: stateItem.orientation,
    }).unwrap()
      .then((result) => {
        dispatch(showToast({kind: "success", title: "Micast-Mirror Einrichtung", caption: "Das Micast-System wurde erfolgreich eingerichtet"}));
        setLoading(false);
        hideModal();
      })
      .catch((error) => {
        dispatch(showToast({kind: "error", title: "Micast-Mirror Einrichtung", caption: `Das Micast-Systm konnte nicht eingrichtet werden. ${error} `}));
        setLoading(false);
      });
  };

  const handleBeforeStepChange = (new_step, old_step) => {
    if (old_step === 1 && new_step === 2) {
      if (stateItem.key.length < 4) {
        setKeyChecked(false);
        return false;
      }

      doCheckDeviceKey(stateItem.key);

      return true;
    }

    if (old_step === 2 && new_step === 3) {
      // label checking
      if (stateItem.label.length < 1) {
        setLabelChecked(false)
        return false;
      }
      setLabelChecked(true);
      return true;

    }

    return true;
  };

  return (
    <WizardForm
      label="Micast-Mirror einrichten"
      onAbort={hideModal}
      onSubmit={handleSubmit}
      onBeforeChangeStep={handleBeforeStepChange}
      isLoading={loading}
      max_steps={3}
      steps={[
        <WizardStep
          label="Identifizieren"
          description="Schritt 1: Schlüssel vom Micast-System"
          validation={keyChecked}
          validation_error={keyCheckFailure}
          key="step_1">
          <FormGroup legendText="">
            <TextInput
              labelText="Micast-Schlüssel"
              value={stateItem.key}
              id="form_create_device_key"
              autoComplete="off"
              autoCorrect="off"
              helperText="Schlüssel für das Micast Gerät (wie in der Anzeige)"
              invalid={
                keyCheckFailure === true || (stateItem.key.length !== 10 && stateItem.key.length > 8)
              }
              invalidText={
                keyCheckFailure === true
                  ? 'Dieser Schlüssel ist dem System nicht bekannt. Bitter überpüfe die Eingabe'
                  : 'Der Schlüssel muss Exakt 10 Zeichen lang sein!'
              }
              onChange={(e) =>
                onChangeField('key', e.target.value.toUpperCase().substr(0, 10))
              }
            />
          </FormGroup>
        </WizardStep>,
        <WizardStep
          label="Einstellungen"
          description="Schritt 2: Name vergeben"
          key="step2"
          validation={labelChecked}
        >
          <FormGroup legendText="">
            <TextInput
              labelText="Micast-Bezeichnung"
              value={stateItem.label}
              id="form_create_device_label"
              autoComplete="off"
              autoCorrect="off"
              invalid={stateItem.label.length < 1}
              invalidText={'Gebe dem Micast System einen Namen'}
              onChange={(e) => onChangeField('label', e.target.value)}
            />
            <FormLabel>Ausrichtung des Micast-Systems</FormLabel>
            <RadioButtonGroup
              orientation="vertical"
              valueSelected={stateItem.orientation + 1 || 1}
              name="form_device_orientation"
              onChange={(e) => onChangeField('orientation', e - 1)}>
              <RadioButton value={1} labelText="Querformat" />
              <RadioButton value={2} labelText="Hochkant Darstellung" />
            </RadioButtonGroup>
            <div className="cds--form__helper-text">
              Lege die Ausrichtung des Systems fest.
            </div>
          </FormGroup>
        </WizardStep>,
        <WizardStep label="Prüfen" description="Schritt 3: Prüfen" key="step3">
          <FormGroup legendText="">
            <p className={cx(styles.SummaryText)}>
              Aktiviere <span>{stateItem.label || ''}</span>
              <br />
              Für das Gerät&nbsp;<span>{mirrorServerName}</span>&nbsp; mit dem
              Schlüssel &nbsp;<span>{stateItem.key}</span>
            </p>
            <br />
          </FormGroup>
        </WizardStep>,
      ]}
    />
  );
};

export default AssignMirrorWizzard;
